/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Breadcrumbs } from '@coursera/cds-core';

export type BreadcrumbLink = { label: string; href: string };

type Props = {
  links: Array<BreadcrumbLink>;
  title: string;
};

const styles = {
  container: css`
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-400);
  `,
};

/**
 * Renders a breadcrumb navigation.
 * @param {Object} props - The properties for the breadcrumb component.
 * @param {string} props.title - The title of the current article page.
 * @param {Array} props.links - The list of breadcrumb links.
 * @param {Object} props.links[].label - The label of the breadcrumb link.
 * @param {string} props.links[].href - The URL of the breadcrumb link.
 * @returns {JSX.Element} The rendered breadcrumb navigation component.
 */
export default function ArticleBreadcrumbs({ title, links }: Props): JSX.Element {
  const retrack = useRetracked();

  return (
    <Breadcrumbs css={styles.container}>
      {links.map(({ label, href }) => {
        const handleClickBreadcrumbItem = () => {
          retrack({
            trackingName: 'breadcrumb_link',
            action: 'click',
            trackingData: { href },
          });
        };
        return (
          <Breadcrumbs.Item key={href} href={href} onClick={handleClickBreadcrumbItem}>
            {label}
          </Breadcrumbs.Item>
        );
      })}
      <Breadcrumbs.Item>{title}</Breadcrumbs.Item>
    </Breadcrumbs>
  );
}
